import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, TrendingUp, Activity, UserCheck, Youtube,
  Clock, Star, Heart, BarChart3, RefreshCw, Mail,
  Chrome, Crown, Radio, Loader, ChevronUp, ChevronDown,
  Search, ChevronLeft, ChevronRight, Shield, X
} from 'lucide-react';
import { getAuthHeaders } from '../services/api';

// ── palette ──────────────────────────────────────────────────────────────────
const c = {
  bg:          '#0a0a0f',
  surface:     '#111117',
  card:        '#16161e',
  border:      'rgba(255,255,255,0.07)',
  borderStrong:'rgba(255,255,255,0.13)',
  text:        '#f4f4f5',
  sub:         '#a1a1aa',
  muted:       '#52525b',
  purple:      '#8B5CF6',
  purpleLight: '#A78BFA',
  pink:        '#EC4899',
  green:       '#22C55E',
  amber:       '#F59E0B',
  blue:        '#3B82F6',
  cyan:        '#06B6D4',
  orange:      '#F97316',
  red:         '#EF4444',
};

const API     = `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/admin/analytics/platform`;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ── tiny helpers ─────────────────────────────────────────────────────────────
const fmt = n => (n == null ? '—' : Number(n).toLocaleString());

const Spinner = () => (
  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'80px 0' }}>
    <Loader size={32} style={{ color: c.purple, animation:'spin 1s linear infinite' }} />
    <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
  </div>
);

// ── KPI card ─────────────────────────────────────────────────────────────────
const KPI = ({ icon: Icon, label, value, sub, accent = c.purple, delta }) => (
  <div style={{
    background: c.card, border:`1px solid ${c.border}`, borderRadius:14,
    padding:'20px 22px', display:'flex', flexDirection:'column', gap:10,
    borderTop:`3px solid ${accent}`,
  }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
      <span style={{ fontSize:12, fontWeight:600, color:c.sub, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</span>
      <div style={{
        width:34, height:34, borderRadius:9, background:`${accent}22`,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <Icon size={17} style={{ color:accent }} />
      </div>
    </div>
    <div style={{ fontSize:30, fontWeight:800, color:c.text, lineHeight:1 }}>{fmt(value)}</div>
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      {delta != null && (
        <span style={{
          fontSize:11, fontWeight:700,
          color: delta >= 0 ? c.green : c.red,
          display:'flex', alignItems:'center', gap:2
        }}>
          {delta >= 0 ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          {Math.abs(delta)}%
        </span>
      )}
      {sub && <span style={{ fontSize:12, color:c.muted }}>{sub}</span>}
    </div>
  </div>
);

// ── horizontal bar ────────────────────────────────────────────────────────────
const Bar = ({ label, value, max, accent = c.purple }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
      <span style={{ width:120, fontSize:12, color:c.sub, flexShrink:0, textOverflow:'ellipsis', overflow:'hidden', whiteSpace:'nowrap' }} title={label}>{label}</span>
      <div style={{ flex:1, height:7, background:'rgba(255,255,255,0.06)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:accent, borderRadius:4, transition:'width 0.5s' }} />
      </div>
      <span style={{ width:38, fontSize:12, color:c.text, fontWeight:700, textAlign:'right', flexShrink:0 }}>{fmt(value)}</span>
    </div>
  );
};

// ── Divider with label ────────────────────────────────────────────────────────
const SectionDivider = ({ icon: Icon, label, accent }) => (
  <div style={{
    display:'flex', alignItems:'center', gap:14, margin:'36px 0 28px',
  }}>
    <div style={{
      width:40, height:40, borderRadius:12,
      background:`linear-gradient(135deg, ${accent}, ${accent}99)`,
      display:'flex', alignItems:'center', justifyContent:'center',
      flexShrink:0, boxShadow:`0 4px 16px ${accent}44`,
    }}>
      <Icon size={20} color="#fff" />
    </div>
    <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:c.text }}>{label}</h2>
    <div style={{ flex:1, height:1, background:c.border }} />
  </div>
);

// ── mini donut ────────────────────────────────────────────────────────────────
function MiniDonut({ slices }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  let angle = -90;
  const r = 44, cx = 50, cy = 50;
  const paths = slices.map(s => {
    const pct = total > 0 ? s.value / total : 0;
    const deg = pct * 360;
    const start = angle;
    angle += deg;
    if (pct === 0) return null;
    const x1 = cx + r * Math.cos((start * Math.PI) / 180);
    const y1 = cy + r * Math.sin((start * Math.PI) / 180);
    const x2 = cx + r * Math.cos(((start + deg) * Math.PI) / 180);
    const y2 = cy + r * Math.sin(((start + deg) * Math.PI) / 180);
    const large = deg > 180 ? 1 : 0;
    return <path key={s.label} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`} fill={s.color} />;
  });
  return (
    <svg viewBox="0 0 100 100" style={{ width:80, height:80, flexShrink:0 }}>
      {total === 0
        ? <circle cx={50} cy={50} r={r} fill={c.border} />
        : paths}
      <circle cx={50} cy={50} r={28} fill={c.card} />
    </svg>
  );
}

// ── signup trend sparkline ────────────────────────────────────────────────────
function SignupTrend({ data }) {
  if (!data || data.length === 0) return <span style={{ color:c.muted, fontSize:12 }}>No data</span>;
  const max = Math.max(...data.map(d => d.count), 1);
  const yAxisW = 44, padR = 20, padT = 32, padB = 36;
  const w = 600, h = 180;
  const chartW = w - yAxisW - padR;
  const chartH = h - padT - padB;
  const gridCount = 4;
  const step = Math.ceil(max / gridCount / 10) * 10 || 1;
  const gridVals = Array.from({ length: gridCount + 1 }, (_, i) => i * step);

  const xOf = (i) => yAxisW + (i / Math.max(data.length - 1, 1)) * chartW;
  const yOf = (v) => padT + chartH - Math.min(v / max, 1) * chartH;
  const pts = data.map((d, i) => `${xOf(i)},${yOf(d.count)}`).join(' ');

  return (
    <div style={{ width:'100%' }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width:'100%', height:'auto', display:'block', overflow:'visible' }}>
        <defs>
          <linearGradient id="visitAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c.purple} stopOpacity={0.35} />
            <stop offset="100%" stopColor={c.purple} stopOpacity={0} />
          </linearGradient>
        </defs>

        {/* grid lines + Y-axis labels */}
        {gridVals.map((val, i) => {
          const y = yOf(val);
          return (
            <g key={i}>
              <line x1={yAxisW} y1={y} x2={w - padR} y2={y}
                stroke="rgba(255,255,255,0.07)" strokeWidth={1} strokeDasharray="4,4" />
              <text x={yAxisW - 8} y={y + 4} textAnchor="end"
                fill="rgba(255,255,255,0.35)" fontSize={10}>{val}</text>
            </g>
          );
        })}

        {/* area fill */}
        <polygon
          points={`${xOf(0)},${padT + chartH} ${pts} ${xOf(data.length - 1)},${padT + chartH}`}
          fill="url(#visitAreaGrad)"
        />

        {/* line */}
        <polyline points={pts} fill="none" stroke={c.purple} strokeWidth={2.5}
          strokeLinejoin="round" strokeLinecap="round" />

        {/* dots + value labels (flip below if near top) */}
        {data.map((d, i) => {
          const x = xOf(i), y = yOf(d.count);
          const nearTop = y < padT + 18;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={4.5} fill={c.purple} stroke={c.bg} strokeWidth={2} />
              <text x={x} y={nearTop ? y + 18 : y - 10} textAnchor="middle"
                fill="rgba(255,255,255,0.8)" fontSize={10} fontWeight="700">{d.count}</text>
            </g>
          );
        })}

        {/* X-axis date labels */}
        {data.map((d, i) => (
          <text key={i} x={xOf(i)} y={h - 8} textAnchor="middle"
            fill="rgba(255,255,255,0.35)" fontSize={10}>{d._id}</text>
        ))}
      </svg>
    </div>
  );
}

// ── channel status badge ──────────────────────────────────────────────────────
const statusColor = { active: c.green, inactive: c.muted, suspended: c.red, deleted: c.red };

// ── main component ────────────────────────────────────────────────────────────
const AdminPlatformAnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Accounts panel state
  const [showAccounts, setShowAccounts] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [acctTotal, setAcctTotal] = useState(0);
  const [acctPage, setAcctPage] = useState(1);
  const [acctTotalPages, setAcctTotalPages] = useState(1);
  const [acctSearch, setAcctSearch] = useState('');
  const [acctSort, setAcctSort] = useState('createdAt');
  const [acctOrder, setAcctOrder] = useState('desc');
  const [acctLoading, setAcctLoading] = useState(false);
  const [acctError, setAcctError] = useState(null);

  // Sub-tab state
  const [subTab, setSubTab] = useState('platform');

  // MAU / investor state
  const [mau, setMau] = useState(null);
  const [mauLoading, setMauLoading] = useState(false);
  const [mauError, setMauError] = useState(null);

  // Creator studio state
  const [studioData, setStudioData] = useState(null);
  const [studioLoading, setStudioLoading] = useState(false);
  const [studioError, setStudioError] = useState(null);
  const [studioSearch, setStudioSearch] = useState('');
  const [expandedCreator, setExpandedCreator] = useState(null);

  // Engagement state
  const [engagement, setEngagement] = useState(null);
  const [engLoading, setEngLoading] = useState(true);
  const [engError, setEngError] = useState(null);
  const [visits, setVisits] = useState(null);

  const load = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true); else setLoading(true);
      setError(null);
      const res = await fetch(API, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed');
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadAccounts = useCallback(async (page, search, sort, order) => {
    setAcctLoading(true);
    setAcctError(null);
    try {
      const params = new URLSearchParams({ page, limit: 20, search, sort, order });
      const res = await fetch(`${API_BASE}/admin/users?${params}`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || `HTTP ${res.status}`);
      setAccounts(json.users);
      setAcctTotal(json.total);
      setAcctTotalPages(json.totalPages);
    } catch (e) {
      setAccounts([]);
      setAcctError(e.message);
    } finally {
      setAcctLoading(false);
    }
  }, []);

  const loadStudio = async () => {
    setStudioLoading(true);
    setStudioError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/analytics/creators-studio`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || `HTTP ${res.status}`);
      setStudioData(json);
    } catch (e) {
      setStudioError(e.message);
    } finally {
      setStudioLoading(false);
    }
  };

  const loadEngagement = async () => {
    try {
      setEngLoading(true);
      setEngError(null);
      const res = await fetch(`${API_BASE}/admin/analytics/engagement`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || `HTTP ${res.status}`);
      setEngagement(json);
    } catch (e) {
      setEngError(e.message);
    } finally {
      setEngLoading(false);
    }
  };

  const loadVisits = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/analytics/visits`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.success) setVisits(json.visits);
    } catch {}
  };

  const loadMau = async () => {
    setMauLoading(true);
    setMauError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/analytics/mau`, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed');
      setMau(json);
    } catch (e) {
      setMauError(e.message);
    } finally {
      setMauLoading(false);
    }
  };

  useEffect(() => { load(); loadEngagement(); loadStudio(); loadVisits(); loadMau(); }, []);

  useEffect(() => {
    if (showAccounts) loadAccounts(acctPage, acctSearch, acctSort, acctOrder);
  }, [showAccounts, acctPage, acctSearch, acctSort, acctOrder, loadAccounts]);

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ padding:40, color:c.red, textAlign:'center', fontSize:14 }}>
      Failed to load analytics: {error}
    </div>
  );

  const { users, creators } = data;

  // Normalize arrays → dicts (server returns [{_id, count}] from aggregation)
  const authProviders = Object.fromEntries(
    (users.authProviders || []).map ? (users.authProviders || []).map(r => [r._id || 'unknown', r.count])
    : Object.entries(users.authProviders || {})
  );
  const reactorTypes = Object.fromEntries(
    (creators.reactorTypeBreakdown || []).map(r => [r._id || 'unknown', r.count])
  );
  const channelStatus = Object.fromEntries(
    (creators.channelStatusBreakdown || []).map(r => [r._id || 'unknown', r.count])
  );

  const signupTotals = users.signupTotals || {
    allTime: users.total || 0,
    sevenDays: users.new7d || 0,
    thirtyDays: users.new30d || 0,
    sixMonths: users.new6m || 0,
    oneYear: users.new1y || 0,
  };

  // auth provider pie
  const authSlices = [
    { label:'Email',  value: (authProviders['local'] || 0) + (authProviders['email'] || 0), color: c.blue   },
    { label:'Google', value: authProviders['google'] || 0, color: c.orange },
  ];

  // reactor type pie
  const reactorSlices = [
    { label:'Solo',   value: reactorTypes['solo']   || 0, color: c.purple },
    { label:'Duo',    value: reactorTypes['duo']    || 0, color: c.pink   },
    { label:'Group',  value: reactorTypes['group']  || 0, color: c.cyan   },
    { label:'Family', value: reactorTypes['family'] || 0, color: c.amber  },
  ].filter(s => s.value > 0);

  const topCatMax = Math.max(...(creators.topCategories || []).map(x => x.count), 1);
  const topEngMax = Math.max(...(users.topEngaged || []).map(u => u.totalVotes || 0), 1);

  return (
    <div style={{ padding:'32px 36px', maxWidth:1280, margin:'0 auto', color:c.text }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:36 }}>
        <div>
          <div>
            <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:c.text }}>Platform Analytics</h1>
            <p style={{ margin:'6px 0 0', fontSize:13, color:c.sub }}>
              User & creator health metrics — live from DB
            </p>
          </div>
          {/* Sub-tabs */}
          <div style={{ display:'flex', gap:6, background:'rgba(255,255,255,0.04)', border:`1px solid ${c.border}`, borderRadius:12, padding:4 }}>
            {[
              { id:'platform', label:'Platform Overview' },
              { id:'creators', label:'Creator Studio' },
              { id:'investor', label:'📊 Investor View' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setSubTab(tab.id)}
                style={{
                  padding:'8px 18px', borderRadius:9, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                  background: subTab === tab.id ? c.purple : 'transparent',
                  color: subTab === tab.id ? '#fff' : c.sub,
                  transition:'all 0.2s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <button
          onClick={() => { load(true); if (subTab === 'creators') loadStudio(); else if (subTab === 'investor') loadMau(); else loadEngagement(); }}
          disabled={refreshing}
          style={{
            display:'flex', alignItems:'center', gap:8, padding:'10px 18px',
            background:`${c.purple}22`, border:`1px solid ${c.purple}55`,
            borderRadius:10, color:c.purpleLight, fontSize:13, fontWeight:600,
            cursor:'pointer', opacity: refreshing ? 0.6 : 1, marginTop:16,
          }}
        >
          <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* ═══════════════ PLATFORM TAB ═══════════════ */}
      {(subTab === 'creators' || subTab === 'investor') ? null : <>

      {/* ═══════════════ SITE VISITS ═══════════════ */}
      <SectionDivider icon={Activity} label="Site Visits" accent={c.cyan} />
      {visits ? (<>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:16, marginBottom:28 }}>
          <KPI icon={Activity} label="Total Visits"    value={visits.total}   accent={c.cyan}   sub="all time" />
          <KPI icon={Activity} label="Today"           value={visits.today}   accent={c.green}  sub="since midnight" />
          <KPI icon={Activity} label="Last 7 Days"     value={visits.last7d}  accent={c.blue}   />
          <KPI icon={Activity} label="Last 30 Days"    value={visits.last30d} accent={c.purple} />
        </div>

        {/* Devices + Referrers */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:32 }}>
          <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 22px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Device Breakdown</div>
            {visits.byDevice.map(d => (
              <Bar key={d._id} label={d._id || 'unknown'} value={d.count}
                max={Math.max(...visits.byDevice.map(x => x.count), 1)}
                accent={d._id === 'mobile' ? c.green : d._id === 'tablet' ? c.amber : c.purple} />
            ))}
          </div>
          <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 22px' }}>
            <div style={{ fontSize:12, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Top Traffic Sources (30d)</div>
            {visits.byReferrer.length === 0
              ? <span style={{ color:c.muted, fontSize:12 }}>No referrer data yet</span>
              : visits.byReferrer.map(r => (
                <Bar key={r._id} label={r._id || 'direct'} value={r.count}
                  max={visits.byReferrer[0]?.count || 1} accent={c.cyan} />
              ))}
          </div>
        </div>

        {/* Daily sparkline */}
        {visits.daily.length > 0 && (
          <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 22px', marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:14 }}>Daily Visits — Last 7 Days</div>
            <SignupTrend data={visits.daily} />
          </div>
        )}
      </>) : <p style={{ color:c.muted, fontSize:13 }}>No visit data yet — starts tracking on first user session.</p>}

      {/* ═══════════════ USERS SECTION ═══════════════ */}
      <SectionDivider icon={Users} label="Platform Users" accent={c.purple} />

      {/* Users KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, marginBottom:28 }}>
        <div
          onClick={() => setShowAccounts(v => !v)}
          style={{ cursor:'pointer', transition:'transform 0.15s', transform: showAccounts ? 'scale(0.98)' : 'scale(1)' }}
          title="Click to view all accounts"
        >
          <KPI icon={Users} label={showAccounts ? 'Total Users — click to collapse ▲' : 'Total Users — click to view list ▼'} value={users.total} accent={c.purple} sub="registered accounts" />
        </div>
        <KPI icon={TrendingUp} label="Signups — 30d"  value={users.new30d}  accent={c.blue}   sub={`${fmt(users.new7d)} in the last 7 days`} />
        <KPI icon={Activity} label="Active — Last 7d"  value={users.active7d} accent={c.green} sub="logged in recently" />
        <KPI icon={Star}     label="Avg Votes / User"  value={users.avgVotes} accent={c.amber} sub="engagement depth" />
      </div>

      {/* ── Accounts panel (expandable) ── */}
      {showAccounts && (
        <div style={{
          background: c.card, border:`1px solid ${c.borderStrong}`,
          borderRadius:14, padding:'24px', marginBottom:28,
          borderTop:`3px solid ${c.purple}`
        }}>
          {/* Panel header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
            <div>
              <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:c.text }}>Registered Accounts</h3>
              <span style={{ fontSize:12, color:c.muted }}>
                {acctTotal.toLocaleString()} total
              </span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {/* Search */}
              <div style={{ position:'relative' }}>
                <Search size={13} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:c.muted }} />
                <input
                  type="text"
                  placeholder="Search username or email…"
                  value={acctSearch}
                  onChange={e => { setAcctSearch(e.target.value); setAcctPage(1); }}
                  style={{
                    padding:'8px 12px 8px 30px', width:220,
                    background:'rgba(255,255,255,0.04)', border:`1px solid ${c.border}`,
                    borderRadius:8, color:c.text, fontSize:13, outline:'none'
                  }}
                />
                {acctSearch && (
                  <button onClick={() => { setAcctSearch(''); setAcctPage(1); }} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:c.muted, padding:0 }}>
                    <X size={12} />
                  </button>
                )}
              </div>
              {/* Sort selector */}
              <select
                value={`${acctSort}:${acctOrder}`}
                onChange={e => { const [s, o] = e.target.value.split(':'); setAcctSort(s); setAcctOrder(o); setAcctPage(1); }}
                style={{
                  padding:'8px 10px', background:'#1c1c26',
                  border:`1px solid ${c.borderStrong}`, borderRadius:8,
                  color:c.text, fontSize:12, outline:'none', cursor:'pointer',
                  appearance:'none', WebkitAppearance:'none'
                }}
              >
                <option value="createdAt:desc" style={{ background:'#1c1c26', color:c.text }}>Newest first</option>
                <option value="createdAt:asc"  style={{ background:'#1c1c26', color:c.text }}>Oldest first</option>
                <option value="lastLogin:desc"  style={{ background:'#1c1c26', color:c.text }}>Last login</option>
                <option value="username:asc"   style={{ background:'#1c1c26', color:c.text }}>Username A→Z</option>
              </select>
            </div>
          </div>

          {/* Table */}
          {acctError && (
            <div style={{ padding:'12px 16px', background:`${c.red}15`, border:`1px solid ${c.red}44`, borderRadius:8, color:c.red, fontSize:13, marginBottom:16 }}>
              Error: {acctError} — restart the local server if testing locally.
            </div>
          )}
          {acctLoading ? (
            <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
              <Loader size={24} style={{ color:c.purple, animation:'spin 1s linear infinite' }} />
            </div>
          ) : (
            <>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                  <thead>
                    <tr style={{ borderBottom:`1px solid ${c.border}` }}>
                      {['User', 'Email', 'Auth', 'Role', 'Verified', 'Joined', 'Last login'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:c.muted, textTransform:'uppercase', letterSpacing:'0.04em', whiteSpace:'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((u, i) => (
                      <tr key={u._id} style={{ borderBottom:`1px solid ${c.border}`, backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                        {/* User */}
                        <td style={{ padding:'10px 12px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{
                              width:28, height:28, borderRadius:7, flexShrink:0,
                              background:`linear-gradient(135deg, ${c.purple}, ${c.pink})`,
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontSize:11, fontWeight:800, color:'#fff'
                            }}>
                              {(u.username || '?')[0].toUpperCase()}
                            </div>
                            <span style={{ fontWeight:600, color:c.text, whiteSpace:'nowrap' }}>{u.username}</span>
                          </div>
                        </td>
                        {/* Email */}
                        <td style={{ padding:'10px 12px', color:c.sub, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</td>
                        {/* Auth */}
                        <td style={{ padding:'10px 12px' }}>
                          <span style={{
                            padding:'2px 8px', borderRadius:5, fontSize:11, fontWeight:700,
                            background: u.authProvider === 'google' ? `${c.orange}22` : `${c.blue}22`,
                            color: u.authProvider === 'google' ? c.orange : c.blue
                          }}>
                            {u.authProvider === 'google' ? 'Google' : 'Email'}
                          </span>
                        </td>
                        {/* Role */}
                        <td style={{ padding:'10px 12px' }}>
                          <span style={{
                            padding:'2px 8px', borderRadius:5, fontSize:11, fontWeight:700,
                            background: u.role === 'admin' ? `${c.red}22` : u.role === 'creator' ? `${c.purple}22` : `${c.muted}22`,
                            color: u.role === 'admin' ? c.red : u.role === 'creator' ? c.purpleLight : c.muted
                          }}>
                            {u.role}
                          </span>
                        </td>
                        {/* Verified */}
                        <td style={{ padding:'10px 12px', textAlign:'center' }}>
                          <span style={{ color: u.isEmailVerified ? c.green : c.muted, fontSize:14 }}>
                            {u.isEmailVerified ? '✓' : '—'}
                          </span>
                        </td>
                        {/* Joined */}
                        <td style={{ padding:'10px 12px', color:c.sub, whiteSpace:'nowrap', fontSize:12 }}>
                          {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                        </td>
                        {/* Last login */}
                        <td style={{ padding:'10px 12px', color:c.sub, whiteSpace:'nowrap', fontSize:12 }}>
                          {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                    {accounts.length === 0 && (
                      <tr><td colSpan={7} style={{ padding:'32px', textAlign:'center', color:c.muted }}>No accounts found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {acctTotalPages > 1 && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:16 }}>
                  <span style={{ fontSize:12, color:c.muted }}>
                    Page {acctPage} of {acctTotalPages} · {acctTotal.toLocaleString()} accounts
                  </span>
                  <div style={{ display:'flex', gap:6 }}>
                    <button
                      onClick={() => setAcctPage(p => Math.max(1, p - 1))}
                      disabled={acctPage === 1}
                      style={{
                        padding:'6px 10px', background:`${c.purple}22`, border:`1px solid ${c.purple}44`,
                        borderRadius:7, color:c.purpleLight, cursor: acctPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: acctPage === 1 ? 0.4 : 1, display:'flex', alignItems:'center', gap:4, fontSize:12
                      }}
                    >
                      <ChevronLeft size={13} /> Prev
                    </button>
                    <button
                      onClick={() => setAcctPage(p => Math.min(acctTotalPages, p + 1))}
                      disabled={acctPage === acctTotalPages}
                      style={{
                        padding:'6px 10px', background:`${c.purple}22`, border:`1px solid ${c.purple}44`,
                        borderRadius:7, color:c.purpleLight, cursor: acctPage === acctTotalPages ? 'not-allowed' : 'pointer',
                        opacity: acctPage === acctTotalPages ? 0.4 : 1, display:'flex', alignItems:'center', gap:4, fontSize:12
                      }}
                    >
                      Next <ChevronRight size={13} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Users row 2: signup trend + auth breakdown + avg engagement */}
      <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:16, marginBottom:28 }}>

        {/* Signup trend */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            30-Day Signup Trend
          </div>
          <SignupTrend data={users.signupTrend} />
        </div>

        {/* Auth provider */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Auth Provider
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:16 }}>
            <MiniDonut slices={authSlices} />
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {authSlices.map(s => (
                <div key={s.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
                  <div style={{ width:8, height:8, borderRadius:'50%', background:s.color, flexShrink:0 }} />
                  <span style={{ fontSize:12, color:c.sub }}>{s.label}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:c.text, marginLeft:'auto' }}>{fmt(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Avg engagement */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Avg Engagement / User
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {[
              { icon: BarChart3, label:'Votes',     value: users.avgVotes,     color: c.purple },
              { icon: Heart,     label:'Favorites',  value: users.avgFavorites, color: c.pink   },
              { icon: Users,     label:'Following',  value: users.avgFollowing, color: c.blue   },
            ].map(({ icon: Icon, label, value, color }) => (
              <div key={label} style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:28, height:28, borderRadius:7, background:`${color}22`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Icon size={13} style={{ color }} />
                </div>
                <span style={{ fontSize:12, color:c.sub, flex:1 }}>{label}</span>
                <span style={{ fontSize:18, fontWeight:800, color:c.text }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:16, marginBottom:28 }}>
        {[
          { label:'All Time', value: signupTotals.allTime, color: c.purple },
          { label:'Last 7 Days', value: signupTotals.sevenDays, color: c.green },
          { label:'Last 30 Days', value: signupTotals.thirtyDays, color: c.blue },
          { label:'Last 6 Months', value: signupTotals.sixMonths, color: c.orange },
          { label:'Last 12 Months', value: signupTotals.oneYear, color: c.pink },
        ].map((item) => (
          <div
            key={item.label}
            style={{
              background:c.card,
              border:`1px solid ${c.border}`,
              borderRadius:14,
              padding:'18px 20px',
              borderTop:`3px solid ${item.color}`
            }}
          >
            <div style={{ fontSize:12, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:10 }}>
              Signups {item.label}
            </div>
            <div style={{ fontSize:28, fontWeight:800, color:c.text, lineHeight:1.05 }}>
              {fmt(item.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Top engaged users */}
      <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px', marginBottom:4 }}>
        <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:18, textTransform:'uppercase', letterSpacing:'0.05em' }}>
          Top Engaged Users (by Votes)
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12 }}>
          {(users.topEngaged || []).map((u, i) => (
            <div key={i} style={{
              background:`${c.purple}0a`, border:`1px solid ${c.border}`,
              borderRadius:10, padding:'12px 14px', display:'flex', alignItems:'center', gap:10,
            }}>
              <div style={{
                width:32, height:32, borderRadius:8,
                background:`linear-gradient(135deg, ${c.purple}, ${c.pink})`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, fontWeight:800, color:'#fff', flexShrink:0,
              }}>
                {(u.username || '?')[0].toUpperCase()}
              </div>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.username || 'Unknown'}</div>
                <div style={{ fontSize:11, color:c.muted }}>{fmt(u.totalVotes)} votes</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ═══════════════ CREATORS SECTION ═══════════════ */}
      <SectionDivider icon={Youtube} label="Creators" accent={c.pink} />

      {/* Creators KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, marginBottom:28 }}>
        <KPI icon={UserCheck} label="Creator Accounts" value={creators.total}          accent={c.pink}   sub="with creator role" />
        <KPI icon={Radio}     label="Claimed Channels" value={creators.claimedChannels} accent={c.purple} sub="linked to creator" />
        <KPI icon={Clock}     label="Waitlist"          value={creators.waitlist}        accent={c.amber}  sub="awaiting access" />
        <KPI icon={Crown}     label="Active Channels"  value={channelStatus['active'] || 0} accent={c.green} sub="status: active" />
      </div>

      {/* Creators row 2: reactor types + top categories + channel status */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1.4fr 1fr', gap:16, marginBottom:28 }}>

        {/* Reactor type donut */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Reactor Types
          </div>
          {reactorSlices.length === 0
            ? <span style={{ color:c.muted, fontSize:12 }}>No data</span>
            : (
              <div style={{ display:'flex', alignItems:'center', gap:16 }}>
                <MiniDonut slices={reactorSlices} />
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {reactorSlices.map(s => (
                    <div key={s.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:s.color, flexShrink:0 }} />
                      <span style={{ fontSize:12, color:c.sub, textTransform:'capitalize' }}>{s.label}</span>
                      <span style={{ fontSize:13, fontWeight:700, color:c.text, marginLeft:'auto' }}>{fmt(s.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          }
        </div>

        {/* Top categories */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Top Content Categories
          </div>
          {(creators.topCategories || []).map(cat => (
            <Bar key={cat._id} label={cat._id || 'Unknown'} value={cat.count} max={topCatMax} accent={c.pink} />
          ))}
        </div>

        {/* Channel status */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Channel Status
          </div>
          {Object.entries(channelStatus).map(([status, count]) => (
            <div key={status} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background: statusColor[status] || c.muted }} />
                <span style={{ fontSize:12, color:c.sub, textTransform:'capitalize' }}>{status}</span>
              </div>
              <span style={{ fontSize:15, fontWeight:700, color:c.text }}>{fmt(count)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top channels + Recent creators */}
      <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:16 }}>

        {/* Top channels */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Top Channels by Subscribers
          </div>
          {(creators.topChannels || []).map((ch, i) => (
            <div key={ch._id} style={{
              display:'flex', alignItems:'center', gap:12, padding:'10px 0',
              borderBottom: i < creators.topChannels.length - 1 ? `1px solid ${c.border}` : 'none',
            }}>
              <span style={{ fontSize:12, fontWeight:700, color:c.muted, width:18, textAlign:'right', flexShrink:0 }}>#{i + 1}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ch.channelName}</div>
                <div style={{ fontSize:11, color:c.muted, textTransform:'capitalize' }}>{ch.reactorType} · {ch.primaryCategory}</div>
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.purpleLight }}>{fmt(ch.subscriberCount)}</div>
                <div style={{ fontSize:10, color:c.muted }}>subscribers</div>
              </div>
              <div style={{
                fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:5,
                background:`${statusColor[ch.status] || c.muted}22`,
                color: statusColor[ch.status] || c.muted,
                textTransform:'capitalize', flexShrink:0,
              }}>
                {ch.status}
              </div>
            </div>
          ))}
          {(!creators.topChannels || creators.topChannels.length === 0) && (
            <span style={{ color:c.muted, fontSize:12 }}>No channels yet</span>
          )}
        </div>

        {/* Recent creators */}
        <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
          <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
            Recent Creator Accounts
          </div>
          {(creators.recentCreators || []).map((cr, i) => (
            <div key={cr._id} style={{
              display:'flex', alignItems:'center', gap:11, padding:'10px 0',
              borderBottom: i < creators.recentCreators.length - 1 ? `1px solid ${c.border}` : 'none',
            }}>
              <div style={{
                width:32, height:32, borderRadius:8, flexShrink:0,
                background:`linear-gradient(135deg, ${c.pink}, ${c.purple})`,
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:13, fontWeight:800, color:'#fff',
              }}>
                {(cr.username || 'C')[0].toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cr.username}</div>
                <div style={{ fontSize:11, color:c.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cr.email}</div>
              </div>
              <div style={{ fontSize:10, color:c.muted, flexShrink:0 }}>
                {new Date(cr.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
          {(!creators.recentCreators || creators.recentCreators.length === 0) && (
            <span style={{ color:c.muted, fontSize:12 }}>No creator accounts yet</span>
          )}
        </div>
      </div>

      {/* ═══════════════ ENGAGEMENT SECTION ═══════════════ */}
      <SectionDivider icon={Activity} label="Platform Engagement" accent={c.cyan} />

      {engLoading ? <Spinner /> : engError ? (
        <div style={{ padding:24, color:c.red, fontSize:13, background:`${c.red}10`, borderRadius:12, marginBottom:28 }}>
          Failed to load engagement data: {engError}
        </div>
      ) : engagement ? (() => {
        const { clicks, content, forum, peakMoments } = engagement;
        const sourceLabels = {
          explore_tab: 'Explore', home_featured: 'Home Featured', peak_moments: 'Peak Moments',
          creator_profile: 'Creator Profile', search_result: 'Search', leaderboard: 'Leaderboard',
          sidebar: 'Sidebar', content_detail: 'Content Detail'
        };
        const typeColors = { movie: c.blue, tv: c.purple, anime: c.pink, music: c.amber };
        const catColors  = { movies: c.blue, 'tv-shows': c.purple, anime: c.pink, 'music-performance': c.amber, 'korean-drama': c.cyan, general: c.muted };
        const maxSrc  = Math.max(...(clicks.bySource      || []).map(x => x.count), 1);
        const maxType = Math.max(...(clicks.byContentType || []).map(x => x.count), 1);
        const maxCat  = Math.max(...(clicks.byCategory    || []).map(x => x.count), 1);
        const maxFav  = Math.max(...(content.topFavorited || []).map(x => x.count), 1);
        const maxFol  = Math.max(...(content.topFollowed  || []).map(x => x.count), 1);
        const maxVot  = Math.max(...(content.topVoted     || []).map(x => x.count), 1);
        const maxLik  = Math.max(...(content.topLiked     || []).map(x => x.count), 1);

        return (
          <>
            {/* Row 1: Click KPIs + section breakdown */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, marginBottom:28 }}>
              <KPI icon={TrendingUp} label="Clicks — Last 30d"   value={clicks.total30d}        accent={c.cyan}   sub="tracked interactions" />
              <KPI icon={Activity}   label="Forum Threads"        value={forum.totalThreads}      accent={c.purple} sub={`+${forum.newThreads30d} this month`} />
              <KPI icon={Star}       label="Peak Moments"         value={peakMoments.total}       accent={c.amber}  sub={`+${peakMoments.new30d} this month`} />
              <KPI icon={Heart}      label="Forum Posts"          value={forum.totalPosts}        accent={c.pink}   sub="total replies" />
            </div>

            {/* Row 2: Platform sections + content type + media category */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:28 }}>

              {/* Clicks by section */}
              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Clicks by Section (30d)
                </div>
                {clicks.bySource.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No click data yet</span>
                  : clicks.bySource.map(s => (
                    <Bar key={s._id} label={sourceLabels[s._id] || s._id} value={s.count} max={maxSrc} accent={c.cyan} />
                  ))
                }
              </div>

              {/* Clicks by content type */}
              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Content Type Interest
                </div>
                {clicks.byContentType.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No data yet</span>
                  : clicks.byContentType.map(s => (
                    <Bar key={s._id} label={s._id || 'unknown'} value={s.count} max={maxType} accent={typeColors[s._id] || c.purple} />
                  ))
                }
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:c.sub, marginBottom:8, textTransform:'uppercase' }}>By Category</div>
                  {clicks.byCategory.length === 0
                    ? <span style={{ color:c.muted, fontSize:12 }}>No data yet</span>
                    : clicks.byCategory.map(s => (
                      <Bar key={s._id} label={s._id || 'unknown'} value={s.count} max={maxCat} accent={catColors[s._id] || c.blue} />
                    ))
                  }
                </div>
              </div>

              {/* Peak Moments by type */}
              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Peak Moment Types
                </div>
                {peakMoments.byType.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No moments yet</span>
                  : peakMoments.byType.map(s => (
                    <div key={s._id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                      <span style={{ fontSize:12, color:c.sub, textTransform:'capitalize' }}>{s._id || 'unknown'}</span>
                      <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                        <span style={{ fontSize:12, color:c.muted }}>{fmt(s.likes)} ❤</span>
                        <span style={{ fontSize:13, fontWeight:700, color:c.text }}>{fmt(s.count)}</span>
                      </div>
                    </div>
                  ))
                }
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:c.sub, marginBottom:8, textTransform:'uppercase' }}>By Category</div>
                  {peakMoments.byCategory.length === 0
                    ? <span style={{ color:c.muted, fontSize:12 }}>No data</span>
                    : peakMoments.byCategory.slice(0, 6).map(s => (
                      <div key={s._id} style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                        <span style={{ fontSize:12, color:c.sub, textTransform:'capitalize' }}>{s._id || 'general'}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:c.text }}>{fmt(s.count)}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>

            {/* Row 3: Top channels by clicks + followed + favorited */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:28 }}>

              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Most Clicked Channels (30d)
                </div>
                {clicks.topChannels.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No data yet</span>
                  : clicks.topChannels.map((ch, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:c.muted, width:16, flexShrink:0 }}>#{i+1}</span>
                      <span style={{ flex:1, fontSize:12, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ch.channelName || ch._id}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:c.cyan, flexShrink:0 }}>{fmt(ch.count)}</span>
                    </div>
                  ))
                }
              </div>

              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Most Followed Channels
                </div>
                {content.topFollowed.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No follows yet</span>
                  : content.topFollowed.map(ch => (
                    <Bar key={ch._id} label={ch.channelName || ch._id} value={ch.count} max={maxFol} accent={c.purple} />
                  ))
                }
              </div>

              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Most Favorited Channels
                </div>
                {content.topFavorited.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No favorites yet</span>
                  : content.topFavorited.map(ch => (
                    <Bar key={ch._id} label={ch.channelName || ch._id} value={ch.count} max={maxFav} accent={c.pink} />
                  ))
                }
              </div>
            </div>

            {/* Row 4: Top voted content + top liked content */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:4 }}>

              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Most Voted Content
                </div>
                {content.topVoted.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No votes yet</span>
                  : content.topVoted.map((item, i) => (
                    <div key={item._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < content.topVoted.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                      <span style={{ fontSize:11, fontWeight:700, color:c.muted, width:16, flexShrink:0 }}>#{i+1}</span>
                      <span style={{
                        padding:'2px 6px', borderRadius:4, fontSize:10, fontWeight:700,
                        background: (typeColors[item.contentType] || c.muted) + '22',
                        color: typeColors[item.contentType] || c.muted, flexShrink:0, textTransform:'capitalize'
                      }}>{item.contentType}</span>
                      <span style={{ flex:1, fontSize:12, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.title}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:c.amber, flexShrink:0 }}>{fmt(item.count)} votes</span>
                    </div>
                  ))
                }
              </div>

              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:14, padding:'20px 24px' }}>
                <div style={{ fontSize:13, fontWeight:700, color:c.sub, marginBottom:16, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                  Most Liked Content
                </div>
                {content.topLiked.length === 0
                  ? <span style={{ color:c.muted, fontSize:12 }}>No likes yet</span>
                  : content.topLiked.map((item, i) => (
                    <div key={item._id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom: i < content.topLiked.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                      <span style={{ fontSize:11, fontWeight:700, color:c.muted, width:16, flexShrink:0 }}>#{i+1}</span>
                      <span style={{
                        padding:'2px 6px', borderRadius:4, fontSize:10, fontWeight:700,
                        background: (typeColors[item.contentType] || c.muted) + '22',
                        color: typeColors[item.contentType] || c.muted, flexShrink:0, textTransform:'capitalize'
                      }}>{item.contentType}</span>
                      <span style={{ flex:1, fontSize:12, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.title}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:c.pink, flexShrink:0 }}>{fmt(item.count)} likes</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </>
        );
      })() : null}

      {/* close platform tab */}
      </>}

      {/* ═══════════════ CREATOR STUDIO TAB ═══════════════ */}
      {subTab === 'creators' && (
        studioLoading ? <Spinner /> :
        studioError   ? <div style={{ padding:24, color:c.red, fontSize:13, background:`${c.red}10`, borderRadius:12 }}>Error: {studioError}</div> :
        studioData    ? (() => {
          const { summary, creators: crList } = studioData;

          const filtered = crList.filter(cr =>
            !studioSearch ||
            cr.username.toLowerCase().includes(studioSearch.toLowerCase()) ||
            cr.email.toLowerCase().includes(studioSearch.toLowerCase()) ||
            (cr.channel?.channelName || '').toLowerCase().includes(studioSearch.toLowerCase())
          );

          const reactorColors = { solo: c.purple, duo: c.pink, group: c.cyan, family: c.amber };

          return (
            <>
              {/* Summary KPIs */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:16, marginBottom:28 }}>
                <KPI icon={UserCheck} label="Total Creators"    value={summary.total}          accent={c.pink}   sub="creator accounts" />
                <KPI icon={Youtube}   label="Claimed Channels"  value={summary.withChannel}    accent={c.purple} sub="linked channel" />
                <KPI icon={Crown}     label="Discovery Boost"   value={summary.withBoost}      accent={c.amber}  sub="active boosts" />
                <KPI icon={TrendingUp} label="Platform Clicks"  value={summary.totalClicks30d} accent={c.cyan}   sub="all creators 30d" />
                <KPI icon={Heart}     label="Total Follows"     value={summary.totalFollows}   accent={c.pink}   sub="on ReactorHub" />
              </div>

              {/* Search */}
              <div style={{ position:'relative', marginBottom:20, maxWidth:360 }}>
                <Search size={14} style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:c.muted }} />
                <input
                  type="text"
                  placeholder="Search creator, email or channel…"
                  value={studioSearch}
                  onChange={e => setStudioSearch(e.target.value)}
                  style={{
                    width:'100%', padding:'10px 12px 10px 34px',
                    background:'rgba(255,255,255,0.04)', border:`1px solid ${c.border}`,
                    borderRadius:10, color:c.text, fontSize:13, outline:'none'
                  }}
                />
              </div>

              {/* Creator cards */}
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {filtered.map(cr => {
                  const isExpanded = expandedCreator === cr.accountId;
                  const s = cr.platformStats;
                  return (
                    <div
                      key={cr.accountId}
                      style={{
                        background: c.card, border:`1px solid ${isExpanded ? c.purple : c.border}`,
                        borderRadius:14, overflow:'hidden',
                        borderLeft: `4px solid ${cr.platformStats.hasBoost ? c.amber : cr.channel ? c.purple : c.muted}`,
                        transition:'border-color 0.2s',
                      }}
                    >
                      {/* Row */}
                      <div
                        onClick={() => setExpandedCreator(isExpanded ? null : cr.accountId)}
                        style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr 1fr 1fr 1fr 1fr auto', gap:16, padding:'14px 20px', cursor:'pointer', alignItems:'center' }}
                      >
                        {/* Creator */}
                        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                          <div style={{
                            width:34, height:34, borderRadius:9, flexShrink:0,
                            background:`linear-gradient(135deg, ${c.pink}, ${c.purple})`,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize:13, fontWeight:800, color:'#fff'
                          }}>
                            {cr.username[0]?.toUpperCase() || '?'}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cr.username}</div>
                            <div style={{ fontSize:11, color:c.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cr.email}</div>
                          </div>
                        </div>

                        {/* Channel */}
                        <div style={{ minWidth:0 }}>
                          {cr.channel ? (
                            <>
                              <div style={{ fontSize:13, fontWeight:600, color:c.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cr.channel.channelName}</div>
                              <div style={{ display:'flex', gap:6, marginTop:2 }}>
                                <span style={{ fontSize:10, fontWeight:700, padding:'1px 6px', borderRadius:4, background:`${reactorColors[cr.channel.reactorType] || c.muted}22`, color:reactorColors[cr.channel.reactorType] || c.muted, textTransform:'capitalize' }}>{cr.channel.reactorType}</span>
                                <span style={{ fontSize:10, color:c.muted, textTransform:'capitalize' }}>{cr.channel.primaryCategory}</span>
                              </div>
                            </>
                          ) : (
                            <span style={{ fontSize:12, color:c.muted, fontStyle:'italic' }}>No channel claimed</span>
                          )}
                        </div>

                        {/* Subscribers */}
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:14, fontWeight:800, color:c.purpleLight }}>{cr.channel ? fmt(cr.channel.subscriberCount) : '—'}</div>
                          <div style={{ fontSize:10, color:c.muted }}>subscribers</div>
                        </div>

                        {/* Clicks 30d */}
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:14, fontWeight:800, color:c.cyan }}>{fmt(s.clicks30d)}</div>
                          <div style={{ fontSize:10, color:c.muted }}>clicks 30d</div>
                        </div>

                        {/* Follows */}
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:14, fontWeight:800, color:c.purple }}>{fmt(s.follows)}</div>
                          <div style={{ fontSize:10, color:c.muted }}>follows</div>
                        </div>

                        {/* Favorites */}
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:14, fontWeight:800, color:c.pink }}>{fmt(s.favorites)}</div>
                          <div style={{ fontSize:10, color:c.muted }}>favorites</div>
                        </div>

                        {/* Boost badge */}
                        <div style={{ textAlign:'center' }}>
                          {s.hasBoost ? (
                            <span style={{ padding:'3px 10px', borderRadius:6, fontSize:11, fontWeight:700, background:`${c.amber}22`, color:c.amber }}>🚀 Boosted</span>
                          ) : (
                            <span style={{ fontSize:11, color:c.muted }}>—</span>
                          )}
                        </div>

                        {/* Expand chevron */}
                        <div style={{ color:c.muted }}>
                          {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                        </div>
                      </div>

                      {/* Expanded: creator studio stats preview */}
                      {isExpanded && (
                        <div style={{
                          borderTop:`1px solid ${c.border}`, padding:'20px 24px',
                          background:'rgba(255,255,255,0.015)'
                        }}>
                          <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:16 }}>
                            Creator Studio — Stats visible to this creator
                          </div>

                          {/* Core stats grid */}
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:12, marginBottom:20 }}>
                            {[
                              { label:'Clicks (30d)',           value: s.clicks30d,            color: c.cyan,   icon:'🖱️' },
                              { label:'Follows',                value: s.follows,              color: c.purple, icon:'👥' },
                              { label:'Favorites',              value: s.favorites,            color: c.pink,   icon:'❤️' },
                              { label:'Community Peak Moments', value: s.peakMoments,          color: c.amber,  icon:'🔥' },
                              { label:'Peak Moment Likes',      value: s.peakMomentLikes,      color: c.red,    icon:'👍' },
                              { label:'Subscribers (YT)',       value: cr.channel?.subscriberCount, color: c.green, icon:'📺' },
                            ].map(stat => (
                              <div key={stat.label} style={{
                                background:c.surface, border:`1px solid ${c.border}`, borderRadius:10,
                                padding:'12px 14px', borderTop:`2px solid ${stat.color}`,
                              }}>
                                <div style={{ fontSize:18, marginBottom:4 }}>{stat.icon}</div>
                                <div style={{ fontSize:18, fontWeight:800, color:c.text }}>{fmt(stat.value)}</div>
                                <div style={{ fontSize:11, color:c.muted, marginTop:2 }}>{stat.label}</div>
                              </div>
                            ))}
                          </div>

                          {/* Appearances & poll row */}
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:20 }}>

                            {/* Featured Carousel */}
                            <div style={{ background:c.surface, border:`1px solid ${c.border}`, borderRadius:10, padding:'14px 16px' }}>
                              <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', marginBottom:10 }}>Featured Carousel</div>
                              <div style={{ fontSize:22, fontWeight:800, color: s.carouselCurrentlyActive ? c.cyan : c.text }}>{fmt(s.carouselAppearances)}</div>
                              <div style={{ fontSize:11, color:c.muted, marginTop:4 }}>times added to carousel</div>
                              <div style={{ marginTop:8, fontSize:11, fontWeight:700, color: s.carouselCurrentlyActive ? c.cyan : c.muted }}>
                                {s.carouselCurrentlyActive ? '✓ Currently featured' : '— Not currently featured'}
                              </div>
                              {s.carouselFirstAdded && (
                                <div style={{ fontSize:11, color:c.muted, marginTop:4 }}>
                                  First added: {new Date(s.carouselFirstAdded).toLocaleDateString()}
                                </div>
                              )}
                            </div>

                            {/* Reaction of the Week */}
                            <div style={{ background:c.surface, border:`1px solid ${c.border}`, borderRadius:10, padding:'14px 16px' }}>
                              <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', marginBottom:10 }}>Reaction of the Week</div>
                              {s.reactionOfWeekSlots.length > 0 ? (
                                <>
                                  <div style={{ fontSize:22, fontWeight:800, color:c.amber }}>{s.reactionOfWeekSlots.length}</div>
                                  <div style={{ fontSize:11, color:c.muted, marginTop:4 }}>current slot(s)</div>
                                  {s.reactionOfWeekSlots.map(r => (
                                    <div key={r.slot} style={{ marginTop:8, fontSize:11, color:c.amber }}>
                                      🏆 Slot #{r.slot} — {r.title || 'No title'}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <>
                                  <div style={{ fontSize:22, fontWeight:800, color:c.muted }}>—</div>
                                  <div style={{ fontSize:11, color:c.muted, marginTop:4 }}>not currently featured</div>
                                  <div style={{ fontSize:10, color:c.muted, marginTop:8, fontStyle:'italic' }}>History not tracked in DB yet</div>
                                </>
                              )}
                            </div>

                            {/* Poll summary */}
                            <div style={{ background:c.surface, border:`1px solid ${c.border}`, borderRadius:10, padding:'14px 16px' }}>
                              <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', marginBottom:10 }}>Poll Appearances</div>
                              <div style={{ display:'flex', gap:16 }}>
                                <div>
                                  <div style={{ fontSize:22, fontWeight:800, color:c.purple }}>{fmt(s.pollAppearances)}</div>
                                  <div style={{ fontSize:10, color:c.muted }}>as channel option</div>
                                </div>
                                <div>
                                  <div style={{ fontSize:22, fontWeight:800, color:c.pink }}>{fmt(s.reactorRequestCount)}</div>
                                  <div style={{ fontSize:10, color:c.muted }}>as requested reactor</div>
                                </div>
                              </div>
                              <div style={{ marginTop:10, display:'flex', gap:16 }}>
                                <div>
                                  <div style={{ fontSize:16, fontWeight:800, color:c.amber }}>{fmt(s.pollVotesReceived)}</div>
                                  <div style={{ fontSize:10, color:c.muted }}>votes received</div>
                                </div>
                                <div>
                                  <div style={{ fontSize:16, fontWeight:800, color:c.cyan }}>{fmt(s.reactorRequestVotes)}</div>
                                  <div style={{ fontSize:10, color:c.muted }}>reactor req. votes</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Poll history */}
                          {s.pollHistory && s.pollHistory.length > 0 && (
                            <div style={{ background:c.surface, border:`1px solid ${c.border}`, borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
                              <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', marginBottom:12 }}>Poll History</div>
                              <div style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                                  <thead>
                                    <tr style={{ borderBottom:`1px solid ${c.border}` }}>
                                      {['Poll', 'Category', 'Votes received', 'Total poll votes', 'Share', 'Status', 'Date'].map(h => (
                                        <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontSize:10, fontWeight:700, color:c.muted, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s.pollHistory.map((p, i) => {
                                      const pct = p.totalPollVotes > 0 ? ((p.voteCount / p.totalPollVotes) * 100).toFixed(1) : '0.0';
                                      return (
                                        <tr key={i} style={{ borderBottom:`1px solid ${c.border}` }}>
                                          <td style={{ padding:'7px 10px', color:c.text, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.pollTitle}</td>
                                          <td style={{ padding:'7px 10px' }}>
                                            <span style={{ padding:'2px 7px', borderRadius:4, fontSize:10, fontWeight:700, background:`${c.purple}22`, color:c.purpleLight, textTransform:'capitalize' }}>{p.category}</span>
                                          </td>
                                          <td style={{ padding:'7px 10px', fontWeight:700, color:c.amber }}>{fmt(p.voteCount)}</td>
                                          <td style={{ padding:'7px 10px', color:c.sub }}>{fmt(p.totalPollVotes)}</td>
                                          <td style={{ padding:'7px 10px', fontWeight:700, color: parseFloat(pct) >= 40 ? c.green : c.sub }}>{pct}%</td>
                                          <td style={{ padding:'7px 10px' }}>
                                            <span style={{ fontSize:10, fontWeight:700, color: p.isActive ? c.green : c.muted }}>{p.isActive ? 'Active' : 'Closed'}</span>
                                          </td>
                                          <td style={{ padding:'7px 10px', color:c.muted, whiteSpace:'nowrap', fontSize:11 }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
                            {/* Account info */}
                            <div>
                              <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', marginBottom:10 }}>Account</div>
                              {[
                                { label:'Email',       value: cr.email },
                                { label:'Joined',      value: cr.joinedAt  ? new Date(cr.joinedAt).toLocaleDateString()  : '—' },
                                { label:'Last login',  value: cr.lastLogin ? new Date(cr.lastLogin).toLocaleDateString() : '—' },
                                { label:'Boost',       value: s.hasBoost ? `Active until ${new Date(s.boostEnds).toLocaleDateString()}` : 'None' },
                              ].map(row => (
                                <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${c.border}` }}>
                                  <span style={{ fontSize:12, color:c.muted }}>{row.label}</span>
                                  <span style={{ fontSize:12, color:c.text, fontWeight:600 }}>{row.value}</span>
                                </div>
                              ))}
                            </div>
                            {/* Channel info */}
                            {cr.channel && (
                              <div>
                                <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', marginBottom:10 }}>Channel</div>
                                {[
                                  { label:'Channel name',   value: cr.channel.channelName },
                                  { label:'Reactor type',   value: cr.channel.reactorType },
                                  { label:'Primary genre',  value: cr.channel.primaryCategory },
                                  { label:'Channel status', value: cr.channel.status },
                                ].map(row => (
                                  <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${c.border}` }}>
                                    <span style={{ fontSize:12, color:c.muted }}>{row.label}</span>
                                    <span style={{ fontSize:12, color:c.text, fontWeight:600, textTransform:'capitalize' }}>{row.value || '—'}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div style={{ padding:40, textAlign:'center', color:c.muted, fontSize:14 }}>No creators match your search</div>
                )}
              </div>
            </>
          );
        })() : null
      )}

      {/* ═══════════════ INVESTOR VIEW TAB ═══════════════ */}
      {subTab === 'investor' && (
        mauLoading ? <Spinner /> :
        mauError ? (
          <div style={{ padding:24, color:c.red, fontSize:13, background:`${c.red}10`, borderRadius:12 }}>
            Error loading MAU data: {mauError}
          </div>
        ) : mau ? (() => {
          const { current, trend, engagement, retention } = mau;
          const momColor   = current.momGrowth == null ? c.muted : current.momGrowth >= 0 ? c.green : c.red;
          const momSign    = current.momGrowth == null ? '' : current.momGrowth >= 0 ? '+' : '';
          const totalEng   = (engagement.casual || 0) + (engagement.regular || 0) + (engagement.power || 0);
          const generatedDate = mau.generatedAt ? new Date(mau.generatedAt).toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' }) : 'today';
          const pitchText  = `ReactorHub — Growth Metrics (as of ${generatedDate})

Monthly Active Users (MAU): ${current.mau.toLocaleString()}
MoM Growth: ${current.momGrowth != null ? `${momSign}${current.momGrowth}%` : 'N/A'}
Daily Active Users (DAU): ${current.dau.toLocaleString()}
DAU/MAU Ratio: ${current.dauMauRatio}%
Total Registered Users: ${current.totalUsers.toLocaleString()}
MAU Penetration: ${current.mauPenetration}% of registered users active in last 30 days

Engagement Quality:
  • Power users (8+ actions/mo): ${engagement.power}
  • Regular users (3–7 actions/mo): ${engagement.regular}
  • Casual users (1–2 actions/mo): ${engagement.casual}

30-Day Retention (users who returned after signup): ${retention.rate}%
New Users This Month: ${current.newUsers30d.toLocaleString()}

Platform: ReactorHub — Reaction content discovery & community
Stage: Beta / Pre-revenue`;

          return (
            <>
              {/* Header row */}
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:32 }}>
                <div>
                  <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:c.text }}>Investor View</h2>
                  <p style={{ margin:'6px 0 0', fontSize:13, color:c.muted }}>
                    Monthly Active Users & growth quality — as of {generatedDate}
                  </p>
                </div>
                <button
                  onClick={() => navigator.clipboard?.writeText(pitchText)}
                  style={{
                    display:'flex', alignItems:'center', gap:6, padding:'9px 16px',
                    background:`${c.amber}18`, border:`1px solid ${c.amber}44`,
                    borderRadius:10, color:c.amber, fontSize:12, fontWeight:700, cursor:'pointer'
                  }}
                >
                  📋 Copy for Pitch Deck
                </button>
              </div>

              {/* Hero KPIs */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16, marginBottom:28 }}>
                <div style={{
                  background: c.card, borderRadius:16, padding:'24px 26px',
                  border:`1px solid ${c.border}`, borderTop:`4px solid ${c.purple}`,
                  position:'relative', overflow:'hidden'
                }}>
                  <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Monthly Active Users</div>
                  <div style={{ fontSize:44, fontWeight:900, color:c.text, lineHeight:1, marginBottom:8 }}>{fmt(current.mau)}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:14, fontWeight:800, color:momColor }}>
                      {current.momGrowth != null ? `${momSign}${current.momGrowth}% MoM` : 'First month'}
                    </span>
                  </div>
                  <div style={{ fontSize:11, color:c.muted, marginTop:4 }}>vs {fmt(current.mauPrev)} prev 30 days</div>
                  <div style={{ position:'absolute', right:20, top:20, fontSize:32, opacity:0.08 }}>👥</div>
                </div>

                <div style={{ background:c.card, borderRadius:16, padding:'24px 26px', border:`1px solid ${c.border}`, borderTop:`4px solid ${c.green}` }}>
                  <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>MAU Penetration</div>
                  <div style={{ fontSize:44, fontWeight:900, color:c.green, lineHeight:1, marginBottom:8 }}>{current.mauPenetration}%</div>
                  <div style={{ fontSize:12, color:c.muted }}>of {fmt(current.totalUsers)} registered users active</div>
                  <div style={{ marginTop:12, height:5, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ width:`${Math.min(current.mauPenetration, 100)}%`, height:'100%', background:c.green, borderRadius:3 }} />
                  </div>
                </div>

                <div style={{ background:c.card, borderRadius:16, padding:'24px 26px', border:`1px solid ${c.border}`, borderTop:`4px solid ${c.cyan}` }}>
                  <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>Daily Active Users</div>
                  <div style={{ fontSize:44, fontWeight:900, color:c.cyan, lineHeight:1, marginBottom:8 }}>{fmt(current.dau)}</div>
                  <div style={{ fontSize:12, color:c.muted }}>active today</div>
                </div>

                <div style={{ background:c.card, borderRadius:16, padding:'24px 26px', border:`1px solid ${c.border}`, borderTop:`4px solid ${c.amber}` }}>
                  <div style={{ fontSize:11, fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>DAU / MAU</div>
                  <div style={{ fontSize:44, fontWeight:900, color:c.amber, lineHeight:1, marginBottom:8 }}>{current.dauMauRatio}%</div>
                  <div style={{ fontSize:12, color:c.muted }}>stickiness ratio</div>
                  <div style={{ marginTop:6, fontSize:11, color: current.dauMauRatio >= 20 ? c.green : current.dauMauRatio >= 10 ? c.amber : c.muted }}>
                    {current.dauMauRatio >= 20 ? '✓ Excellent (>20%)' : current.dauMauRatio >= 10 ? '~ Good (>10%)' : 'Building'}
                  </div>
                </div>
              </div>

              {/* 6-month trend chart */}
              <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:16, padding:'24px 26px', marginBottom:24 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:800, color:c.text }}>6-Month Engaged User Trend</div>
                    <div style={{ fontSize:11, color:c.muted, marginTop:3 }}>Distinct users who voted or posted per calendar month</div>
                  </div>
                  <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background:`${c.purple}22`, color:c.purpleLight, fontWeight:700 }}>
                    Engaged MAU
                  </span>
                </div>
                <SignupTrend data={trend.map(t => ({ _id: t.month, count: t.count }))} />
              </div>

              {/* Engagement + Retention + New users row */}
              <div style={{ display:'grid', gridTemplateColumns:'1.2fr 1fr 1fr', gap:20, marginBottom:24 }}>

                {/* Engagement quality */}
                <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:16, padding:'22px 24px' }}>
                  <div style={{ fontSize:13, fontWeight:800, color:c.text, marginBottom:4 }}>Engagement Quality</div>
                  <div style={{ fontSize:11, color:c.muted, marginBottom:20 }}>User segments by actions in last 30 days</div>
                  {[
                    { label:'Power Users',   value:engagement.power,   color:c.purple, desc:'8+ votes/month',  icon:'🔥' },
                    { label:'Regular Users', value:engagement.regular, color:c.blue,   desc:'3–7 votes/month', icon:'⭐' },
                    { label:'Casual Users',  value:engagement.casual,  color:c.muted,  desc:'1–2 votes/month', icon:'👋' },
                  ].map(seg => {
                    const pct = totalEng > 0 ? Math.round((seg.value / totalEng) * 100) : 0;
                    return (
                      <div key={seg.label} style={{ marginBottom:16 }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                            <span style={{ fontSize:14 }}>{seg.icon}</span>
                            <span style={{ fontSize:12, fontWeight:700, color:c.text }}>{seg.label}</span>
                            <span style={{ fontSize:11, color:c.muted }}>({seg.desc})</span>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontSize:13, fontWeight:800, color:seg.color }}>{fmt(seg.value)}</span>
                            <span style={{ fontSize:11, color:c.muted, width:32, textAlign:'right' }}>{pct}%</span>
                          </div>
                        </div>
                        <div style={{ height:6, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:seg.color, borderRadius:3, transition:'width 0.5s' }} />
                        </div>
                      </div>
                    );
                  })}
                  {totalEng === 0 && <div style={{ fontSize:12, color:c.muted, textAlign:'center', paddingTop:8 }}>No voting activity yet</div>}
                </div>

                {/* Retention */}
                <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:16, padding:'22px 24px' }}>
                  <div style={{ fontSize:13, fontWeight:800, color:c.text, marginBottom:4 }}>30-Day Retention</div>
                  <div style={{ fontSize:11, color:c.muted, marginBottom:20 }}>Of users who signed up 30–60 days ago, how many returned?</div>
                  <div style={{ textAlign:'center', marginBottom:20 }}>
                    <div style={{ fontSize:56, fontWeight:900, color: retention.rate >= 30 ? c.green : retention.rate >= 15 ? c.amber : c.orange, lineHeight:1 }}>
                      {retention.rate}%
                    </div>
                    <div style={{ fontSize:12, color:c.muted, marginTop:8 }}>
                      {fmt(retention.retained)} of {fmt(retention.cohortSize)} users returned
                    </div>
                  </div>
                  <div style={{ height:8, background:'rgba(255,255,255,0.06)', borderRadius:4, overflow:'hidden' }}>
                    <div style={{
                      width:`${Math.min(retention.rate, 100)}%`, height:'100%', borderRadius:4,
                      background: retention.rate >= 30 ? c.green : retention.rate >= 15 ? c.amber : c.orange,
                      transition:'width 0.5s'
                    }} />
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginTop:16 }}>
                    {[
                      { label:'Good',      value:'>30%', color:c.green },
                      { label:'Average',   value:'>15%', color:c.amber },
                      { label:'Building',  value:'<15%', color:c.orange },
                    ].map(b => (
                      <div key={b.label} style={{ textAlign:'center' }}>
                        <div style={{ fontSize:10, color:b.color, fontWeight:700 }}>{b.value}</div>
                        <div style={{ fontSize:10, color:c.muted }}>{b.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* New users + totals */}
                <div style={{ background:c.card, border:`1px solid ${c.border}`, borderRadius:16, padding:'22px 24px' }}>
                  <div style={{ fontSize:13, fontWeight:800, color:c.text, marginBottom:4 }}>Growth Signals</div>
                  <div style={{ fontSize:11, color:c.muted, marginBottom:20 }}>Key acquisition & base metrics</div>
                  {[
                    { label:'Total Registered',  value:fmt(current.totalUsers),  color:c.purple, icon:'📦' },
                    { label:'New This Month',     value:fmt(current.newUsers30d), color:c.green,  icon:'🆕' },
                    { label:'Active (MAU)',        value:fmt(current.mau),         color:c.blue,   icon:'✅' },
                    { label:'Active Today (DAU)', value:fmt(current.dau),         color:c.cyan,   icon:'🟢' },
                  ].map(row => (
                    <div key={row.label} style={{
                      display:'flex', alignItems:'center', justifyContent:'space-between',
                      padding:'10px 0', borderBottom:`1px solid ${c.border}`
                    }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontSize:16 }}>{row.icon}</span>
                        <span style={{ fontSize:12, color:c.sub }}>{row.label}</span>
                      </div>
                      <span style={{ fontSize:16, fontWeight:800, color:row.color }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pitch-deck summary */}
              <div style={{
                background:`linear-gradient(135deg, ${c.purple}12, ${c.pink}08)`,
                border:`1px solid ${c.purple}33`, borderRadius:16, padding:'24px 28px'
              }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:c.text }}>Pitch Deck Summary</div>
                  <button
                    onClick={() => navigator.clipboard?.writeText(pitchText)}
                    style={{
                      padding:'6px 14px', background:`${c.purple}22`, border:`1px solid ${c.purple}44`,
                      borderRadius:8, color:c.purpleLight, fontSize:11, fontWeight:700, cursor:'pointer'
                    }}
                  >
                    Copy text
                  </button>
                </div>
                <pre style={{
                  margin:0, fontFamily:'monospace', fontSize:12, color:c.sub,
                  whiteSpace:'pre-wrap', lineHeight:'1.8', background:'rgba(0,0,0,0.3)',
                  borderRadius:10, padding:'16px 18px', border:`1px solid ${c.border}`
                }}>
                  {pitchText}
                </pre>
              </div>
            </>
          );
        })() : null
      )}

    </div>
  );
};

export default AdminPlatformAnalyticsTab;
